<!-- Workspace Dropdown -->
<div class="workspace-dropdown-container bg-gray-100 m-2 rounded-base">
    <app-popover #workspacePopover [width]="'20vw'" [maxHeight]="'40vh'" [showScrollbar]="true">
        <ng-template #trigger>
            <div
                class="flex items-center justify-between  gap-2 p-2 w-full rounded-base shadow-sm min-w-[10rem] cursor-pointer hover:border-neutral-300">
                <div class="flex items-center gap-2">
                    @if(currentWorkspace?.name) {
                    <span class=" text-white px-2 rounded-base"
                        [style.background-color]="currentWorkspace?.color">{{currentWorkspace?.name?.charAt(0)?.toUpperCase()
                        || ''}}</span>
                    }
                    <span class="current-workspace">{{ currentWorkspace?.name || 'Select Workspace' }}</span>
                </div>
                <i class="pi pi-angle-down workspace-arrow"></i>
            </div>
        </ng-template>
        <ng-template #header>
            <div class="workspace-search-container">
                <app-input type="text" placeholder="Search workspaces..." [(ngModel)]="searchText" [class]="'w-full'">
                </app-input>
            </div>
        </ng-template>
        <ng-template #body>
            <div class="workspace-items-container">
                @for (item of filteredWorkspaces; track $index) {
                <div class="workspace-item"
                    [class.current-workspace-item]="item.workspace._id === currentWorkspace?._id"
                    (click)="selectWorkspace(item.workspace); workspacePopover.hide()">
                    <div class="workspace-info">
                        <span class="workspace-name">
                            {{ item.label }}
                        </span>
                        <div class="workspace-meta" *ngIf="item.workspace.type === WORKSPACE_TYPE.CUSTOM">
                            <i class="pi pi-users workspace-users-icon"></i>
                            <span class="user-count">{{ item.userCount }}</span>
                        </div>
                    </div>
                    <i class="pi workspace-edit-icon" [class.pi-pencil]="canEditWorkspace(item.workspace._id)"
                        [class.pi-eye]="!canEditWorkspace(item.workspace._id)"
                        (click)="openWorkspace(item, $event); $event.stopPropagation()"></i>
                </div>
                }
            </div>
        </ng-template>
        <ng-template #footer>
            @if(canCreateWorkspace() && hasAnyInnDocFeatures()) {
            <div class="workspace-item create-workspace-item"
                (click)="openCreateWorkspaceDialog(); workspacePopover.hide()">
                <div class="workspace-info">
                    <span class="workspace-name">
                        <i class="pi pi-plus create-icon"></i>
                        {{'Create Workspace' | textCase}}
                    </span>
                </div>
            </div>
            }
        </ng-template>
    </app-popover>
</div>

<!-- Create/Edit Workspace Dialog -->
<app-dialog [visible]="createWorkspaceDialogVisible" [title]="dialogHeader" [fullscreenOnMobile]="true"
    class="max-w-[45rem]" (closed)="closeCreateWorkspaceDialog()">

    <ng-template #bodyTemplate>
        <div class="create-workspace-content px-6 py-4">
            <p class="workspace-description">
                {{
                'Workspaces allow organizations to create a centralized environment within Inncircles to manage
                RFPs,documents, and workflows.' | textCase
                }}
            </p>

            @if(this.selectedWorkspace?.type != WORKSPACE_TYPE.DEFAULT) {
            <!-- Progress Steps -->
            <div class="progress-steps">
                <div class="step" [class.active]="activeStep <= 1">
                    <div class="step-number">1</div>
                    <span class="step-label">{{'Workspace Details' | textCase}}</span>
                </div>
                <div class="step-line"></div>
                <div class="step" [class.active]="activeStep === 1">
                    <div class="step-number">2</div>
                    <span class="step-label">{{'Team Setup' | textCase}}</span>
                </div>
            </div>
            }

            <!-- Workspace Form -->
            @if(activeStep === 0) {
            <form [formGroup]="workspaceForm" class="workspace-form" [class.pointer-events-none]="nextLoading"
                [class.opacity-60]="nextLoading">
                <div class="form-group">
                    <label for="workspaceName" class="form-label required">{{'Workspace Name' | textCase}}</label>
                    <input type="text" id="workspaceName" formControlName="name" class="form-input"
                        [readonly]="selectedWorkspace?.type === WORKSPACE_TYPE.DEFAULT || !canEditWorkspace(selectedWorkspace?._id) || nextLoading"
                        placeholder="e.g., ABC Infra Pvt Ltd">
                </div>

                <div class="form-group">
                    <label for="workspaceDescription" class="form-label">{{'Description (Optional)' | textCase}}</label>
                    <textarea id="workspaceDescription" formControlName="description" class="form-textarea"
                        [disabled]="!canEditWorkspace(selectedWorkspace?._id)"
                        placeholder="Briefly describe your workspace and its purpose." rows="3"></textarea>
                </div>

                <div class="form-group">
                    <label class="form-label">{{'Workspace Color (Optional)' | textCase}}</label>
                    <div class="color-picker">
                        <div *ngFor="let color of workspaceColors" class="color-option"
                            [class.selected]="workspaceForm.get('color')?.value === color.value"
                            [style.background-color]="color.color"
                            (click)="canEditWorkspace(selectedWorkspace?._id) && !nextLoading ? selectColor(color.value) : null"
                            [title]="color.label">
                        </div>
                    </div>
                </div>
            </form>
            } @else if(activeStep === 1) {
            <div class="flex flex-col gap-3" [class.pointer-events-none]="nextLoading" [class.opacity-60]="nextLoading">
                <app-share-to-users [disabled]="!canEditWorkspace(selectedWorkspace?._id) || nextLoading"
                    [shareDetails]="shareDetails" [workspace]="workspaceForShareComponent"
                    (removeUserFromShare)="removeUserFromShare($event)"></app-share-to-users>
            </div>
            }
        </div>
    </ng-template>

    <ng-template #footerTemplate>
        @if(this.selectedWorkspace?.type === WORKSPACE_TYPE.DEFAULT) {
        <div class="flex justify-end">
            <app-button label="{{'Save' | textCase}}" icon="pi pi-save" (clicked)="saveDefaultWorkspace()"
                variant="filled" severity="primary" [disabled]="!workspaceForm.valid" />
        </div>
        } @else {
        <div class="flex justify-between gap-2">
            @if(this.selectedWorkspace?._id && this.activeStep === 0 && canDeleteWorkspace()) {
            <app-button label="{{'Delete Workspace' | textCase}}" (clicked)="deleteWorkspace()" variant="filled"
                severity="danger" />
            } @else if(this.activeStep === 1) {
            <app-button label="{{'Previous' | textCase}}" (clicked)="activeStep = 0" variant="outlined"
                severity="contrast" />
            }
            @if(shouldShowNextButton) {
            <div class="flex justify-end">
                <app-button [label]="nextButtonLabel | textCase" (clicked)="nextStep()" variant="filled"
                    severity="primary" [disabled]="!workspaceForm.valid" [loading]="nextLoading" />
            </div>
            }
        </div>
        }
    </ng-template>
</app-dialog>

<!-- Confirmation Dialog -->
<app-delete-confirmation [(visible)]="deleteConfirmDialogVisible" headerText="Are you sure?"
    bodyText="This action will delete the workspace permanently" confirmButtonText="Delete"
    (onConfirm)="confirmDeleteWorkspace()">
</app-delete-confirmation>